// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// ================================================================
// TestDataScrubber — Reusable component for any Forge app
//
// Drop this into any app to scan and purge test/demo data.
// Works with any Supabase-backed app using is_deleted soft-delete.
//
// Usage:
//   import TestDataScrubber from '../components/TestDataScrubber';
//   <TestDataScrubber supabase={supabase} companyId={user.company_id} />
//
// Convention: when creating test data, set metadata.is_test = true
// or prefix names with [TEST], [DEMO], or [SAMPLE].
// This scrubber detects both conventions.
// ================================================================
import React, { useState, useCallback } from 'react';

// Tables to scan — add more as needed per app
const DEFAULT_TABLES = [
  { name: 'master_jobs', nameCol: 'service_description', filterCol: 'company_id', softDelete: 'is_deleted' },
  { name: 'customers', nameCol: 'customer_name', filterCol: 'company_id', softDelete: 'is_deleted' },
  { name: 'quotes', nameCol: 'title', filterCol: 'company_id', softDelete: 'is_deleted' },
  { name: 'invoices', nameCol: 'invoice_number', filterCol: 'company_id', softDelete: 'is_deleted' },
  { name: 'contacts', nameCol: 'full_name', filterCol: 'company_id', softDelete: 'is_deleted' },
];

// Patterns that indicate test data (case-insensitive)
const TEST_PATTERNS = [
  /^\[test\]/i, /^\[demo\]/i, /^\[sample\]/i, /^\[fake\]/i,
  /^test\s/i, /^demo\s/i, /^sample\s/i, /^fake\s/i,
  /test\s*customer/i, /test\s*job/i, /test\s*quote/i,
  /demo\s*customer/i, /demo\s*job/i,
  /john\s*doe/i, /jane\s*doe/i, /lorem\s*ipsum/i,
  /asdf/i, /qwerty/i, /xxx/i,
];

function isTestRecord(record, nameCol) {
  // Check metadata flag first
  if (record.metadata?.is_test === true) return true;
  if (record.is_test === true) return true;
  // Check name patterns
  const name = record[nameCol] || '';
  return TEST_PATTERNS.some(p => p.test(name));
}

export default function TestDataScrubber({ supabase, companyId, tables = DEFAULT_TABLES, onComplete }) {
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [purging, setPurging] = useState(false);
  const [selected, setSelected] = useState({});
  const [purged, setPurged] = useState(false);

  const scan = useCallback(async () => {
    if (!companyId) return;
    setScanning(true);
    setPurged(false);
    const found = {};

    for (const table of tables) {
      try {
        const query = supabase
          .from(table.name)
          .select(`id, ${table.nameCol}, created_at, metadata`)
          .eq(table.filterCol, companyId);

        // Only get non-deleted if soft delete exists
        if (table.softDelete) {
          query.eq(table.softDelete, false);
        }

        const { data, error } = await query.limit(500);
        if (error) {
          console.warn(`Scrubber: skip ${table.name}:`, error.message);
          continue;
        }

        const testRecords = (data || []).filter(r => isTestRecord(r, table.nameCol));
        if (testRecords.length > 0) {
          found[table.name] = { table, records: testRecords };
        }
      } catch (e) {
        console.warn(`Scrubber: error scanning ${table.name}:`, e.message);
      }
    }

    setResults(found);
    // Select all by default
    const sel = {};
    Object.entries(found).forEach(([tbl, { records }]) => {
      sel[tbl] = records.map(r => r.id);
    });
    setSelected(sel);
    setScanning(false);
  }, [supabase, companyId, tables]);

  const toggleRecord = (tableName, id) => {
    setSelected(prev => {
      const list = prev[tableName] || [];
      return {
        ...prev,
        [tableName]: list.includes(id) ? list.filter(x => x !== id) : [...list, id],
      };
    });
  };

  const toggleAll = (tableName) => {
    const records = results[tableName]?.records || [];
    const allSelected = (selected[tableName] || []).length === records.length;
    setSelected(prev => ({
      ...prev,
      [tableName]: allSelected ? [] : records.map(r => r.id),
    }));
  };

  const purge = async () => {
    setPurging(true);
    let totalPurged = 0;

    for (const [tableName, ids] of Object.entries(selected)) {
      if (!ids || ids.length === 0) continue;
      const table = results[tableName]?.table;
      if (!table) continue;

      try {
        if (table.softDelete) {
          // Soft delete
          const { error } = await supabase
            .from(tableName)
            .update({ [table.softDelete]: true })
            .in('id', ids);
          if (!error) totalPurged += ids.length;
        } else {
          // Hard delete if no soft delete column
          const { error } = await supabase
            .from(tableName)
            .delete()
            .in('id', ids);
          if (!error) totalPurged += ids.length;
        }
      } catch (e) {
        console.error(`Scrubber: purge error on ${tableName}:`, e);
      }
    }

    setPurged(true);
    setPurging(false);
    if (totalPurged > 0 && onComplete) onComplete(totalPurged);
  };

  const totalFound = results ? Object.values(results).reduce((s, r) => s + r.records.length, 0) : 0;
  const totalSelected = Object.values(selected).reduce((s, ids) => s + ids.length, 0);

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <span style={{ fontSize: 28 }}>🧹</span>
        <div>
          <div style={{ fontWeight: 800, fontSize: 18, color: '#1F2937' }}>Test Data Scrubber</div>
          <div style={{ fontSize: 13, color: '#6B7280' }}>
            Scans for test/demo/fake records and removes them safely (soft-delete)
          </div>
        </div>
      </div>

      {!results && !scanning && (
        <button onClick={scan} style={{
          padding: '12px 24px', borderRadius: 10, border: 'none',
          background: '#3B82F6', color: '#fff', fontSize: 15, fontWeight: 700,
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
        }}>
          🔍 Scan for Test Data
        </button>
      )}

      {scanning && (
        <div style={{ padding: 20, textAlign: 'center', color: '#6B7280' }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🔍</div>
          <div style={{ fontWeight: 600 }}>Scanning tables for test data...</div>
        </div>
      )}

      {results && !purged && (
        <div>
          {totalFound === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', background: '#F0FDF4', borderRadius: 12, border: '1px solid #BBF7D0' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>✅</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#15803D' }}>No test data found!</div>
              <div style={{ fontSize: 13, color: '#166534', marginTop: 4 }}>Your database is clean.</div>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 16, padding: '10px 14px', background: '#FEF2F2', borderRadius: 8, border: '1px solid #FECACA', fontSize: 13, color: '#991B1B' }}>
                Found <strong>{totalFound}</strong> test record{totalFound !== 1 ? 's' : ''} across {Object.keys(results).length} table{Object.keys(results).length !== 1 ? 's' : ''}. Review and select which to remove.
              </div>

              {Object.entries(results).map(([tableName, { table, records }]) => (
                <div key={tableName} style={{ marginBottom: 16, border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 14px', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
                  }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1F2937' }}>
                      {tableName} <span style={{ color: '#DC2626', fontWeight: 800 }}>({records.length})</span>
                    </div>
                    <button onClick={() => toggleAll(tableName)} style={{
                      background: 'none', border: '1px solid #D1D5DB', borderRadius: 6,
                      padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: '#374151',
                    }}>
                      {(selected[tableName] || []).length === records.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div style={{ maxHeight: 250, overflowY: 'auto' }}>
                    {records.map(r => {
                      const isSelected = (selected[tableName] || []).includes(r.id);
                      return (
                        <div key={r.id}
                          onClick={() => toggleRecord(tableName, r.id)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
                            borderBottom: '1px solid #F3F4F6', cursor: 'pointer',
                            background: isSelected ? '#FEF2F2' : '#fff',
                          }}>
                          <input type="checkbox" checked={isSelected} readOnly
                            style={{ accentColor: '#DC2626', width: 16, height: 16 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {r[table.nameCol] || `ID: ${r.id}`}
                            </div>
                            <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                              Created: {r.created_at ? new Date(r.created_at).toLocaleDateString() : 'Unknown'}
                              {r.metadata?.is_test ? ' — Tagged as test' : ' — Pattern match'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button onClick={purge} disabled={purging || totalSelected === 0} style={{
                  padding: '12px 24px', borderRadius: 10, border: 'none',
                  background: totalSelected > 0 ? '#DC2626' : '#9CA3AF',
                  color: '#fff', fontSize: 14, fontWeight: 700,
                  cursor: totalSelected > 0 ? 'pointer' : 'not-allowed',
                }}>
                  {purging ? 'Purging...' : `🗑 Remove ${totalSelected} Record${totalSelected !== 1 ? 's' : ''}`}
                </button>
                <button onClick={() => { setResults(null); setSelected({}); }} style={{
                  padding: '12px 24px', borderRadius: 10, border: '1px solid #D1D5DB',
                  background: '#fff', fontSize: 14, cursor: 'pointer', color: '#374151',
                }}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {purged && (
        <div style={{ padding: 24, textAlign: 'center', background: '#F0FDF4', borderRadius: 12, border: '1px solid #BBF7D0' }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>✅</div>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#15803D' }}>Test data purged!</div>
          <div style={{ fontSize: 13, color: '#166534', marginTop: 4 }}>
            {totalSelected} record{totalSelected !== 1 ? 's' : ''} soft-deleted. Refresh pages to see clean data.
          </div>
          <button onClick={() => { setResults(null); setSelected({}); setPurged(false); }} style={{
            marginTop: 12, padding: '8px 16px', borderRadius: 8, border: '1px solid #BBF7D0',
            background: '#fff', fontSize: 13, cursor: 'pointer', color: '#15803D', fontWeight: 600,
          }}>
            Scan Again
          </button>
        </div>
      )}

      <div style={{ marginTop: 20, padding: '12px 14px', background: '#F0F9FF', borderRadius: 8, border: '1px solid #BAE6FD', fontSize: 12, color: '#0369A1' }}>
        <strong>Tip:</strong> When creating test data in any app, prefix names with <code>[TEST]</code>, <code>[DEMO]</code>, or <code>[SAMPLE]</code> so this scrubber catches them automatically. Or set <code>metadata.is_test = true</code> in the record.
      </div>
    </div>
  );
}
